if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        this.touchStartX = 0;
        this.boundHandleNavigationClick = this.handleNavigationClick.bind(this);
        this.boundHandleKeydown = this.handleKeydown.bind(this);
        this.boundHandleTouchStart = this.handleTouchStart.bind(this);
        this.boundHandleTouchEnd = this.handleTouchEnd.bind(this);
      }

      connectedCallback() {
        super.connectedCallback();

        this.addEventListener('click', this.boundHandleNavigationClick);
        this.addEventListener('keydown', this.boundHandleKeydown);
        this.addEventListener('touchstart', this.boundHandleTouchStart, { passive: true });
        this.addEventListener('touchend', this.boundHandleTouchEnd, { passive: true });
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.boundHandleNavigationClick);
        this.removeEventListener('keydown', this.boundHandleKeydown);
        this.removeEventListener('touchstart', this.boundHandleTouchStart);
        this.removeEventListener('touchend', this.boundHandleTouchEnd);
      }

      hide() {
        super.hide();
      }

      show(opener) {
        this.openedBy = opener;
        document.body.classList.add('overflow-hidden');
        this.setAttribute('open', '');

        const mediaId = opener?.getAttribute('data-media-id');
        this.setActiveMediaById(mediaId);
        this.updateNavigation();

        const dialog = this.querySelector('[role="dialog"]');
        trapFocus(this, dialog);
        window.pauseAllMedia();
      }

      handleNavigationClick(event) {
        const trigger = event.target.closest('[data-product-modal-action]');
        if (!trigger) return;

        event.preventDefault();
        event.stopPropagation();

        const modalAction = trigger.dataset.productModalAction;

        if (modalAction === 'prev') this.goToPrevious();
        if (modalAction === 'next') this.goToNext();
      }

      handleKeydown(event) {
        if (!this.hasAttribute('open')) return;

        if (event.code === 'ArrowLeft') {
          event.preventDefault();
          this.goToPrevious();
        }

        if (event.code === 'ArrowRight') {
          event.preventDefault();
          this.goToNext();
        }
      }

      handleTouchStart(event) {
        this.touchStartX = event.changedTouches[0].screenX;
      }

      handleTouchEnd(event) {
        const touchEndX = event.changedTouches[0].screenX;
        const swipeDistance = touchEndX - this.touchStartX;
        const swipeThreshold = 50;

        if (Math.abs(swipeDistance) < swipeThreshold) return;

        if (swipeDistance > 0) {
          this.goToPrevious();
        } else {
          this.goToNext();
        }
      }

      goToPrevious() {
        const navigableMediaItems = this.getNavigableMediaItems();
        const activeIndex = this.getActiveIndex(navigableMediaItems);

        if (activeIndex <= 0) return;

        const previousMedia = navigableMediaItems[activeIndex - 1];
        this.setActiveMediaById(previousMedia.getAttribute('data-media-id'));
        this.updateNavigation();
      }

      goToNext() {
        const navigableMediaItems = this.getNavigableMediaItems();
        const activeIndex = this.getActiveIndex(navigableMediaItems);

        if (activeIndex >= navigableMediaItems.length - 1) return;

        const nextMedia = navigableMediaItems[activeIndex + 1];
        this.setActiveMediaById(nextMedia.getAttribute('data-media-id'));
        this.updateNavigation();
      }

      setActiveMediaById(mediaId) {
        const navigableMediaItems = this.getNavigableMediaItems();
        const resolvedMediaId = mediaId || navigableMediaItems[0]?.getAttribute('data-media-id');

        if (!resolvedMediaId) return;

        const currentActiveMedia = this.querySelector('[data-media-id].active');
        const isChangingMedia = currentActiveMedia?.getAttribute('data-media-id') !== resolvedMediaId;

        if (isChangingMedia) {
          window.pauseAllMedia();
        }

        this.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.toggle('active', element.getAttribute('data-media-id') === resolvedMediaId);
        });

        const activeMedia = this.querySelector(`[data-media-id="${resolvedMediaId}"]`);
        if (!activeMedia) return;

        if (activeMedia.nodeName === 'DEFERRED-MEDIA' || activeMedia.nodeName === 'PRODUCT-MODEL') {
          activeMedia.loadContent?.(false);
        }
      }

      updateNavigation() {
        const navigableMediaItems = this.getNavigableMediaItems();
        const activeIndex = this.getActiveIndex(navigableMediaItems);
        const navigation = this.querySelector('.product-media-modal__navigation');
        const prevButton = this.querySelector('[data-product-modal-action="prev"]');
        const nextButton = this.querySelector('[data-product-modal-action="next"]');

        if (navigation) {
          navigation.hidden = navigableMediaItems.length <= 1;
        }

        if (prevButton) {
          prevButton.disabled = activeIndex <= 0;
        }

        if (nextButton) {
          nextButton.disabled = activeIndex >= navigableMediaItems.length - 1;
        }
      }

      getNavigableMediaItems() {
        const content = this.querySelector('[role="document"]');
        if (!content) return [];

        const mediaItems = [...content.children].filter((element) => element.hasAttribute('data-media-id'));
        const variantMediaItems = mediaItems.filter((element) =>
          element.classList.contains('product__media-item--variant')
        );

        return mediaItems.filter((element) => {
          if (!element.classList.contains('product__media-item--variant')) return true;
          return element === variantMediaItems[0];
        });
      }

      getActiveIndex(navigableMediaItems) {
        return navigableMediaItems.findIndex((element) => element.classList.contains('active'));
      }

      showActiveMedia() {
        const mediaId = this.openedBy?.getAttribute('data-media-id');
        this.setActiveMediaById(mediaId);
        this.updateNavigation();
      }
    }
  );
}
